// Generic QueryBuilder implementation for all models
// This file is auto-generated - do not edit manually

import { NeoGraphParser } from './neograph-utils';
import { graphStore, GraphStore } from './graph-store';
import { queryCache, QueryResultData } from './query-cache';
import { getFrameworkResolvers } from './resolvers';
import { resolveRelProperty } from './reverse-property';

export class QueryBuilderBase {
  // Static default fetch function set by client initialization
  private static defaultFetchFn?: (path: string, options?: RequestInit) => Promise<any>;

  // ModelRegistry injected at runtime to avoid circular dependency:
  // query-builder-base → model-registry → api/* → query-builder-base
  private static _modelRegistry: Record<string, any> | null = null;

  /**
   * Inject ModelRegistry at runtime to break circular dependency.
   * Called once during client initialization.
   */
  static setModelRegistry(registry: Record<string, any>) {
    QueryBuilderBase._modelRegistry = registry;
  }

  constructor(
    protected modelName: string,
    protected path: string[] = [],
    protected params: Record<string, any> = {},
    protected fetchFn?: (path: string, options?: RequestInit) => Promise<any>,
    protected queryName?: string,
    protected scopedParams: Record<string, Record<string, any>> = {},
    protected queryPathMap: Record<string, string> = {},
    protected BuilderClass?: new (...args: any[]) => any
  ) {}

  /**
   * Set the default fetch function for all query builders
   * Called once during client initialization
   */
  static setDefaultFetch(fn: (path: string, options?: RequestInit) => Promise<any>) {
    QueryBuilderBase.defaultFetchFn = fn;
  }

  protected getCurrentScope(): string {
    const operations = ['filter', 'order_by', 'limit', 'offset'];
    const relationships = this.path.filter(p => !operations.includes(p));
    return relationships.length > 0 ? relationships[relationships.length - 1] : 'source';
  }

  protected createBuilder(
    modelName: string,
    path: string[],
    params: Record<string, any>,
    fetchFn?: (path: string, options?: RequestInit) => Promise<any>,
    queryName?: string,
    scopedParams: Record<string, Record<string, any>> = {}
  ): any {
    const BuilderClass = this.BuilderClass || QueryBuilderBase;
    return new BuilderClass(
      modelName,
      path,
      params,
      fetchFn,
      queryName,
      scopedParams,
      this.queryPathMap,
      this.BuilderClass
    );
  }

  filter(conditions: Record<string, any>): any {
    const scope = this.getCurrentScope();
    const newScopedParams = { ...this.scopedParams };
    newScopedParams[scope] = { ...(newScopedParams[scope] || {}), ...conditions };
    return this.createBuilder(
      this.modelName,
      [...this.path, 'filter'],
      { ...this.params, ...conditions },
      this.fetchFn,
      this.queryName,
      newScopedParams
    );
  }


  orderBy(field: string): any {
    const scope = this.getCurrentScope();
    const newScopedParams = { ...this.scopedParams };
    // Backend expects order_by as an array of field strings
    newScopedParams[scope] = { ...(newScopedParams[scope] || {}), order_by: [field] };
    return this.createBuilder(
      this.modelName,
      [...this.path, 'order_by'],
      { ...this.params, order_by: [field] },
      this.fetchFn,
      this.queryName,
      newScopedParams
    );
  }

  limit(value: number): any {
    const scope = this.getCurrentScope();
    const newScopedParams = { ...this.scopedParams };
    newScopedParams[scope] = { ...(newScopedParams[scope] || {}), limit: value };
    return this.createBuilder(
      this.modelName,
      [...this.path, 'limit'],
      { ...this.params, limit: value },
      this.fetchFn,
      this.queryName,
      newScopedParams
    );
  }

  offset(value: number): any {
    const scope = this.getCurrentScope();
    const newScopedParams = { ...this.scopedParams };
    newScopedParams[scope] = { ...(newScopedParams[scope] || {}), offset: value };
    return this.createBuilder(
      this.modelName,
      [...this.path, 'offset'],
      { ...this.params, offset: value },
      this.fetchFn,
      this.queryName,
      newScopedParams
    );
  }

  load(include: Record<string, any>): any {
    const newScopedParams = { ...this.scopedParams };
    newScopedParams['include'] = include;
    return this.createBuilder(
      this.modelName,
      this.path,
      this.params,
      this.fetchFn,
      this.queryName,
      newScopedParams
    );
  }

  async all(fetchFn?: (path: string, options?: RequestInit) => Promise<any>): Promise<any[]> {
    const fn = fetchFn || this.fetchFn || QueryBuilderBase.defaultFetchFn;
    if (!fn) {
      throw new Error('No fetch function configured. Call QueryBuilderBase.setDefaultFetch() during client initialization.');
    }

    // Resolve query name from path if not set
    let resolvedQueryName = this.queryName;
    if (!resolvedQueryName) {
      const pathKey = this.path.join('|');
      resolvedQueryName = this.queryPathMap[pathKey];
      if (!resolvedQueryName) {
        throw new Error(`No registered query found for path: ${pathKey}`);
      }
    }

    // Build query request in expected format
    const queryRequest = {
      query_name: resolvedQueryName,
      params: this.scopedParams,
      format: 'neograph'
    };

    // Call the backend prepared query API
    const response = await fn('/api/query', {
      method: 'POST',
      body: JSON.stringify(queryRequest),
    });

    // Backend returns neograph format: { root_nodes: [...], nodes: [...], relationships: [...] }
    // Generate queryKey from query parameters for caching
    const queryKey = this.generateQueryKey(resolvedQueryName, this.scopedParams);

    // Populate both GraphStore (unordered Sets) and QueryCache (ordered Arrays)
    // This constructs node instances ONCE and stores them in GraphStore
    this.populateGraphStore(response, queryKey);

    // Get the already-constructed instances from GraphStore (no double construction!)
    const rootNodeIds = response.root_nodes?.map((n: any) => n.id) || [];
    const instances = rootNodeIds
      .map((id: string) => graphStore.getNode(id))
      .filter((node: any): node is any => node !== undefined);

    return instances;
  }

  /**
   * Generate a stable query key from query name and parameters
   * Used to cache query results in QueryCache
   * @private
   */
  private generateQueryKey(queryName: string, params: Record<string, Record<string, any>>): string {
    // Sort keys for stable serialization
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        const scopeParams = params[key];
        acc[key] = Object.keys(scopeParams)
          .sort()
          .reduce((scopeAcc, scopeKey) => {
            scopeAcc[scopeKey] = scopeParams[scopeKey];
            return scopeAcc;
          }, {} as Record<string, any>);
        return acc;
      }, {} as Record<string, Record<string, any>>);

    return `${queryName}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Populate GraphStore with nodes and relationships from query response
   * This enables reactive updates via Jotai atoms
   * @private
   */
  private populateGraphStore(response: any, queryKey: string): void {
    try {
      // Register all nodes (root_nodes + nodes) in GraphStore
      const allNodes = [...(response.root_nodes || []), ...(response.nodes || [])];

      // Create Set of root node IDs for O(1) lookup (Bug fix #2)
      const rootNodeSet = new Set(response.root_nodes?.map((n: any) => n.id) || []);
      const rootNodeIds: string[] = [];

      for (const nodeData of allNodes) {
        if (!nodeData.id) continue;

        // Track root nodes for QueryCache (O(1) lookup)
        if (rootNodeSet.has(nodeData.id)) {
          rootNodeIds.push(nodeData.id);
        }

        // Instantiate the node using injected ModelRegistry
        const label = nodeData._label;
        const ModelClass = label ? QueryBuilderBase._modelRegistry?.[label] : null;

        if (ModelClass && ModelClass.load) {
          try {
            const nodeInstance = ModelClass.load(nodeData);
            graphStore.updateNode(nodeInstance);
          } catch (error) {
            console.warn(`Failed to load node ${nodeData.id}:`, error);
          }
        }
      }

      // Build QueryCache data structure
      // Track relationships grouped by (sourceNodeId, propertyName) for correct scoping
      const relationshipsByTraversal = new Map<string, string[]>();

      // Register all relationships in GraphStore and group for QueryCache
      for (const rel of response.relationships || []) {
        if (!rel.source || !rel.target || !rel.rel) continue;

        // The backend no longer ships rel_property / rel_dest_property — edges
        // are emitted in canonical origin->dest orientation, so source always
        // uses its `out` property and target its `in`, resolved type-aware from
        // the node types (resolves polymorphic rels the flat scalars can't, e.g.
        // Project.physical_parts vs PhysicalPart.children).
        const resolvers = getFrameworkResolvers();
        const relClass = resolvers.getRelationshipClass(rel.rel);
        const sourceLabel = (graphStore.getNode(rel.source) as any)?._label;
        const targetLabel = (graphStore.getNode(rel.target) as any)?._label;
        const forwardPropertyName = resolveRelProperty(
          resolvers.modelRegistry?.[sourceLabel], rel.rel, 'out', relClass,
        );
        const reversePropertyName = resolveRelProperty(
          resolvers.modelRegistry?.[targetLabel], rel.rel, 'in', relClass,
        );

        if (!forwardPropertyName || !reversePropertyName) {
          console.warn('Unknown relationship type:', rel.rel);
          continue;
        }

        // Edge properties ride on the response when the include hop requested
        // `_rel_props` — pass them through so `rel.properties` reads work for
        // query-loaded data too (same as the DAG snapshot/patch paths).
        const edgeProps = (rel as any).properties as Record<string, any> | undefined;

        // Add FORWARD index: source → origin_property → [target]
        // Query data is marked as NOT from DAG source - DAG can overwrite this
        graphStore.addRelationship(rel.source, rel.target, forwardPropertyName, undefined, { isDagSource: false, properties: edgeProps });

        // Add REVERSE index: target → dest_property → [source]
        graphStore.addRelationship(rel.target, rel.source, reversePropertyName, undefined, { isDagSource: false, properties: edgeProps });

        // Group relationships by (sourceNodeId:propertyName) for QueryCache (Bug fix #1)
        // This ensures each source node's relationships are tracked separately
        const traversalPath = `${rel.source}:${forwardPropertyName}`;
        if (!relationshipsByTraversal.has(traversalPath)) {
          relationshipsByTraversal.set(traversalPath, []);
        }
        relationshipsByTraversal.get(traversalPath)!.push(GraphStore.relationshipId(rel.source, forwardPropertyName, rel.target));
      }

      // Store in QueryCache with metadata
      const queryResultData: QueryResultData = {
        nodeIds: rootNodeIds,
        relationships: relationshipsByTraversal,
        metadata: {
          filters: this.scopedParams,
          orderBy: this.scopedParams.source?.order_by || [],
          limit: this.scopedParams.source?.limit,
          skip: this.scopedParams.source?.offset,
          complete: true, // Assume complete unless we implement pagination
          stale: false,
          ttl: new Date(Date.now() + 5 * 60 * 1000), // 5 minutes
        },
      };

      queryCache.setQueryResult(queryKey, queryResultData);
    } catch (error) {
      console.error('Error populating GraphStore from query:', error);
    }
  }

  async first(fetchFn?: (path: string, options?: RequestInit) => Promise<any>): Promise<any | null> {
    const results = await this.all(fetchFn);
    return results[0] || null;
  }

  async execute(fetchFn?: (path: string, options?: RequestInit) => Promise<any[]>): Promise<any[]> {
    return this.all(fetchFn);
  }

  /**
   * Context-aware getter that returns single or array based on expected cardinality.
   * For relationships: uses relationship cardinality
   * For source queries: uses whether single result is expected
   *
   * Note: The actual return type is determined by the generated interface,
   * this implementation just delegates to all() or first() as appropriate.
   */
  async get(fetchFn?: (path: string, options?: RequestInit) => Promise<any>): Promise<any> {
    // For now, we delegate to all() and let the interface define the return type
    // The Python generator will create specific overloads with correct types
    // TODO: In the future, we could track cardinality metadata here
    return this.all(fetchFn);
  }
}
